export interface TeamSettings { name: string; amount: number; }
export interface SavedTeamSettings extends TeamSettings { companyId: string; teamId: string; managed: boolean; }
export interface TeamSettingsRequest { settings: TeamSettings; baseline: TeamSettings; }
export interface TeamSettingsApi {
  getWorkforceTeamSettings(companyId: string, teamId: string): Promise<SavedTeamSettings>;
  saveWorkforceTeamSettings(companyId: string, teamId: string, request: TeamSettingsRequest): Promise<SavedTeamSettings>;
}
export function settingsCents(value: unknown): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) { return null; }
  const match = /^(0|[1-9][0-9]*)(?:\.([0-9]{1,2}))?$/.exec(String(value));
  if (!match) { return null; }
  const result = Number(match[1]) * 100 + Number((match[2] || '').padEnd(2, '0'));
  return Number.isSafeInteger(result) && result <= 99999999 ? result : null;
}
export class WorkforceTeamSettingsState {
  baseline: TeamSettings | null = null;
  name = '';
  amount: number | null = null;
  busy = false;
  error = '';
  needsReload = false;
  private version = 0;
  private companyId = '';
  private teamId = '';

  constructor(private api: TeamSettingsApi) {}

  get valid(): boolean {
    const name = this.name.trim();
    return !!name && name.length <= 80 && !/[\x00-\x1f\x7f]/.test(name) && settingsCents(this.amount) !== null;
  }

  destroy(): void { ++this.version; this.baseline = null; }

  async open(companyId: string, teamId: string): Promise<void> {
    const version = ++this.version;
    this.companyId = companyId;
    this.teamId = teamId;
    this.baseline = null;
    this.busy = true;
    this.error = '';
    this.needsReload = false;
    try {
      const current = this.checked(await this.api.getWorkforceTeamSettings(companyId, teamId));
      if (version !== this.version) { return; }
      this.baseline = {name: current.name, amount: current.amount};
      this.name = current.name;
      this.amount = current.amount;
    } catch (_) {
      if (version === this.version) { this.error = 'Settings could not be loaded. Reload to try again.'; this.needsReload = true; }
    } finally { if (version === this.version) { this.busy = false; } }
  }

  async reload(): Promise<void> { if (!this.busy) { await this.open(this.companyId, this.teamId); } }

  async save(): Promise<SavedTeamSettings | null> {
    if (this.busy || !this.baseline || !this.valid || this.needsReload) { return null; }
    const version = this.version;
    const requested = {name: this.name.trim(), amount: Number(this.amount)};
    const request = {settings: requested, baseline: {...this.baseline}};
    this.busy = true;
    this.error = '';
    try {
      const saved = this.checked(await this.api.saveWorkforceTeamSettings(this.companyId, this.teamId, request));
      return version === this.version ? saved : null;
    } catch (error: any) {
      if (version !== this.version) { return null; }
      const code = error?.data?.code || error?.error?.code;
      if (code === 'settings_changed') {
        this.error = 'These settings changed while you were editing. Reload before saving.';
        this.needsReload = true;
      } else if (['team_busy', 'team_write_uncertain', 'access_denied', 'team_not_managed', 'request_invalid'].includes(code)) {
        this.error = code === 'team_busy' ? 'Another team change is in progress. Try Save again after it finishes.'
          : code === 'team_write_uncertain' ? 'A previous team change needs review before settings can be saved.'
          : 'Settings could not be saved. Reload to check access and current values.';
        this.needsReload = code !== 'team_busy';
      } else {
        // A transport/server failure may have followed commit. Read only; never
        // replace the original baseline and automatically resubmit the old edit.
        try {
          const current = this.checked(await this.api.getWorkforceTeamSettings(this.companyId, this.teamId));
          if (version !== this.version) { return null; }
          if (current.name === requested.name && settingsCents(current.amount) === settingsCents(requested.amount)) { return current; }
          this.error = 'The save could not be confirmed. Current settings differ. Reload before editing again.';
        } catch (_) {
          if (version !== this.version) { return null; }
          this.error = 'The save could not be confirmed and settings could not be reread. Reload to check before saving again.';
        }
        this.needsReload = true;
      }
      return null;
    } finally { if (version === this.version) { this.busy = false; } }
  }

  private checked(value: SavedTeamSettings): SavedTeamSettings {
    if (!value || value.companyId !== this.companyId || value.teamId !== this.teamId || value.managed !== true
      || typeof value.name !== 'string' || settingsCents(value.amount) === null) { throw new Error('Invalid settings response'); }
    return value;
  }
}
